import React, { useEffect, useMemo, useState } from "react";
import { ChatMessageList } from "./ChatMessageList";
import { ChatMessageInput } from "./ChatMessageInput";
import { useChatStore } from "@/store/chat";
import { ChatMessage } from "@/types/chat";
import { useAuthStore } from "@/store/authStore";
import { StoreSelector } from "@/components/menu/StoreSelector";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { trackEvent } from "@/services/analytics";
import { useTranslation } from "react-i18next";

export const ChatContainer: React.FC = () => {
  const { user } = useAuthStore();
  const {
    messages,
    loading,
    fetchMessages,
    sendMessage,
    setupRealtimeSubscription,
    // setCurrentStoreId,
    // currentStoreId,
    markAsRead,
    initializeMessages,
  } = useChatStore();
  const { stores, fetchStores, fetchEmployees, employees, selectedStoreId } =
    useStoreManagementStore();
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const { t } = useTranslation();
  useEffect(() => {
    const initChat = async () => {
      await fetchStores();
      await fetchEmployees(selectedStoreId);
      await fetchMessages(selectedStoreId);
    };

    initChat();
  }, [fetchStores, selectedStoreId]);

  // Set up realtime subscription for current store
  useEffect(() => {
    if (!selectedStoreId) return;

    const unsubscribe = setupRealtimeSubscription(selectedStoreId);

    return () => {
      unsubscribe();
    };
  }, [selectedStoreId, setupRealtimeSubscription]);

  // Mark messages as read when component mounts and when active
  useEffect(() => {
    if (selectedStoreId) {
      markAsRead();
    }

    // Mark as read when window gains focus
    const handleFocus = () => {
      if (selectedStoreId) {
        markAsRead();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [selectedStoreId, markAsRead]);

  const handleSendMessage = async (
    content: string,
    messageType: "text" | "image" | "video" | "system",
    attachmentUrl?: string,
    replyTo?: string
  ) => {
    if (!selectedStoreId || !user) {
      toast({
        title: "Error",
        description: "You need to select a store first",
        variant: "destructive",
      });
      return;
    }

    try {
      const success = await sendMessage(
        selectedStoreId,
        content,
        messageType,
        attachmentUrl
      );

      if (success) {
        setReplyTo(null);

        // Track successful message send with detailed properties
        trackEvent("chat_message_sent_success", {
          message_type: messageType,
          has_attachment: !!attachmentUrl,
          is_reply: !!replyTo,
          store_id: selectedStoreId,
          content_length: content?.length || 0,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });

        // Track failed message send
        trackEvent("chat_message_send_failed", {
          message_type: messageType,
          store_id: selectedStoreId,
          has_attachment: !!attachmentUrl,
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleStoreChange = (storeId: string) => {
    // setCurrentStoreId(storeId);

    // Track store change in chat
    trackEvent("chat_store_changed", {
      store_id: storeId,
      from_store_id: selectedStoreId,
    });
  };

  const storeStaffCount = useMemo(
    () => employees.filter((emp) => emp.storeId === selectedStoreId).length,
    [employees, selectedStoreId]
  );

  // Generate a system message when a new user joins
  const handleSystemMessage = async () => {
    if (!selectedStoreId || !user) return;

    const joinMessage = `${user.name || user.email || "User"} joined the chat`;
    await sendMessage(selectedStoreId, joinMessage, "system");
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] md:h-[calc(100vh-4rem)] bg-gray-50 dark:bg-gray-900">
      <div className="p-3 border-b bg-white dark:bg-gray-950 shadow-sm">
        <StoreSelector
          isSelectable={false}
          value={selectedStoreId || ""}
          onValueChange={handleStoreChange}
          disabled={false}
          isInChat={employees.length > 0}
          noOfEmployees={employees.length}
        />
      </div>

      {loading ? (
        <div className="flex-1 p-4 space-y-4">
          <Skeleton className="h-16 w-3/4 rounded-lg" />
          <Skeleton className="h-16 w-1/2 rounded-lg ml-auto" />
          <Skeleton className="h-16 w-2/3 rounded-lg" />
        </div>
      ) : !selectedStoreId ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">
            Select a store to start chatting
          </p>
        </div>
      ) : (
        <>
          <ChatMessageList messages={messages} onReply={setReplyTo} />

          <ChatMessageInput
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            onSendMessage={handleSendMessage}
          />
        </>
      )}
    </div>
  );
};
